// Precondition: None
// Postcondition: Compact inline header with search button and user actions
// Invariant: Shadow DOM encapsulates styles; uses ft-search-button for search
class AppHeader extends HTMLElement {
  private abortController = new AbortController();
  private isMenuOpen = false;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback(): void {
    this.render();
    this.setupEventListeners();
  }

  disconnectedCallback(): void {
    this.abortController.abort();
  }

  private render(): void {
    if (!this.shadowRoot) return;

    this.shadowRoot.innerHTML = `
      <style>${this.getStyles()}</style>
      ${this.getTemplate()}
    `;
  }

  private setupEventListeners(): void {
    this.abortController.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    // Hamburger toggle
    this.shadowRoot?.querySelector(".hamburger")?.addEventListener(
      "click",
      () => this.toggleMenu(),
      { signal }
    );

    // Close on outside click
    document.addEventListener(
      "click",
      (e) => {
        if (this.isMenuOpen && !e.composedPath().includes(this)) {
          this.closeMenu();
        }
      },
      { signal }
    );

    // Close on Escape
    document.addEventListener(
      "keydown",
      (e) => {
        if (e.key === "Escape" && this.isMenuOpen) {
          e.preventDefault();
          this.closeMenu();
          this.shadowRoot?.querySelector<HTMLButtonElement>(".hamburger")?.focus();
        }
      },
      { signal }
    );
  }

  private toggleMenu(): void {
    this.isMenuOpen ? this.closeMenu() : this.openMenu();
  }

  private openMenu(): void {
    this.isMenuOpen = true;
    this.classList.add("menu-open");
    this.shadowRoot?.querySelector(".hamburger")?.setAttribute("aria-expanded", "true");
  }

  private closeMenu(): void {
    this.isMenuOpen = false;
    this.classList.remove("menu-open");
    this.shadowRoot?.querySelector(".hamburger")?.setAttribute("aria-expanded", "false");
  }

  private getStyles(): string {
    return `
      :host {
        display: block;
      }

      .header {
        display: flex;
        align-items: center;
      }

      .left-wrapper {
        width: 32%;
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .center-wrapper {
        width: 32%;
        display: flex;
        justify-content: center;
      }

      .right-wrapper {
        position: relative;
        width: 36%;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 8px;
      }

      ::slotted(*) {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      /* Hamburger trigger - hidden on desktop */
      .hamburger {
        display: none;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        padding: 0;
        border: 1px solid var(--input-border, #E2E4E8);
        border-radius: var(--radius, 6px);
        background: var(--input-bg, #ffffff);
        cursor: pointer;
        transition: border-color 150ms ease-out, background 150ms ease-out;
      }

      .hamburger:hover {
        border-color: var(--input-border-focus, #999999);
        background: var(--tab-bg, #f4f4f5);
      }

      .hamburger:focus {
        outline: none;
        border-color: var(--input-border-focus, #999999);
        box-shadow: 0 0 0 2px var(--focus-ring, rgba(0, 0, 0, 0.1));
      }

      /* Hamburger icon lines */
      .hamburger-icon {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 3px;
        width: 16px;
        height: 16px;
      }

      .hamburger-line {
        display: block;
        width: 14px;
        height: 2px;
        background: var(--text, #1C1C1E);
        border-radius: 1px;
        transition: transform 200ms ease-out, opacity 150ms ease-out;
        will-change: transform, opacity;
      }

      /* Hamburger to X animation */
      :host(.menu-open) .hamburger-line:nth-child(1) {
        transform: translateY(5px) rotate(45deg);
      }

      :host(.menu-open) .hamburger-line:nth-child(2) {
        opacity: 0;
        transform: scaleX(0);
      }

      :host(.menu-open) .hamburger-line:nth-child(3) {
        transform: translateY(-5px) rotate(-45deg);
      }

      /* Overflow menu panel */
      .overflow-menu {
        display: none;
        position: absolute;
        top: calc(100% + 8px);
        right: 0;
        min-width: 200px;
        padding: 8px;
        opacity: 0;
        visibility: hidden;
        transform: translateY(-8px) scale(0.95);
        transform-origin: top right;
        transition:
          opacity 150ms ease-out,
          transform 150ms ease-out,
          visibility 150ms;
        z-index: 100;
        border-radius: var(--radius, 6px);
        background: var(--input-bg, #ffffff);
        border: 1px solid var(--input-border, #E2E4E8);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
        flex-direction: column;
        gap: 4px;
      }

      .overflow-menu ::slotted(*) {
        flex-direction: column;
        align-items: stretch;
        width: 100%;
        gap: 4px;
      }

      :host(.menu-open) .overflow-menu {
        opacity: 1;
        visibility: visible;
        transform: translateY(0) scale(1);
      }

      /* Mobile breakpoint */
      @media (max-width: 640px) {
        .left-wrapper,
        .center-wrapper {
          width: auto;
          flex: 1;
        }

        .right-wrapper {
          width: auto;
          flex: 0 0 auto;
        }

        /* Hide inline right slot, show hamburger */
        .right-inline {
          display: none;
        }

        .hamburger {
          display: flex;
        }

        .overflow-menu {
          display: flex;
        }
      }

      /* Dark mode */
      @media (prefers-color-scheme: dark) {
        .hamburger {
          background: var(--input-bg, #14161a);
          border-color: var(--input-border, #272a31);
        }

        .hamburger:hover {
          background: var(--tab-bg, #1f2328);
        }

        .hamburger-line {
          background: var(--text, #ffffff);
        }

        .overflow-menu {
          background: var(--input-bg, #14161a);
          border-color: var(--input-border, #272a31);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .hamburger-line,
        .hamburger,
        .overflow-menu {
          transition: none;
        }
      }
    `;
  }

  private getTemplate(): string {
    return `
      <header class="header">
        <div class="left-wrapper">
          <slot name="left"></slot>
        </div>
        <div class="center-wrapper">
          <slot name="center"></slot>
        </div>
        <div class="right-wrapper">
          <div class="right-inline">
            <slot name="right"></slot>
          </div>
          <button
            class="hamburger"
            type="button"
            aria-label="Menu"
            aria-haspopup="menu"
            aria-expanded="false">
            <span class="hamburger-icon">
              <span class="hamburger-line"></span>
              <span class="hamburger-line"></span>
              <span class="hamburger-line"></span>
            </span>
          </button>
          <div class="overflow-menu" role="menu">
            <slot name="overflow"></slot>
          </div>
        </div>
      </header>
    `;
  }
}

// Guard against duplicate registration
if (!customElements.get("ft-app-header")) {
  customElements.define("ft-app-header", AppHeader);
}
